import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import AdultGate from '@/components/AdultGate';
import { MusicNotes, MoonStars, Sparkle, Plus, X, Shield } from '@phosphor-icons/react';

const THEMES = [
  { id: 'jazz_lounge',   label: 'Jazz Lounge',    glow: 'from-amber-400 to-rose-500' },
  { id: 'techno',         label: 'Techno',          glow: 'from-fuchsia-500 to-cyan-400' },
  { id: 'burlesque',      label: 'Burlesque',       glow: 'from-rose-500 to-amber-300' },
  { id: 'after_party',    label: 'After-Party',     glow: 'from-violet-500 to-fuchsia-400' },
  { id: 'rooftop',        label: 'Rooftop',         glow: 'from-cyan-400 to-blue-500' },
  { id: 'underground',    label: 'Underground',     glow: 'from-fuchsia-600 to-black' },
  { id: 'private_suite',  label: 'Private Suite',   glow: 'from-rose-400 to-violet-600' },
];

const fmtAgo = (iso) => {
  const m = (Date.now() - new Date(iso).getTime()) / 60000;
  if (m < 60) return `${Math.floor(m)}m`;
  return `${Math.floor(m / 60)}h`;
};

const CreateClubModal = ({ open, onClose, onCreated }) => {
  const [title, setTitle] = useState('');
  const [theme, setTheme] = useState('jazz_lounge');
  const [bubbles, setBubbles] = useState([]);
  const [bubbleId, setBubbleId] = useState('');
  const [duration, setDuration] = useState(4);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    api.get('/bubbles/mine').then(({ data }) => {
      setBubbles(data.bubbles || []);
      if (data.bubbles?.[0]) setBubbleId(data.bubbles[0].id);
    });
  }, [open]);

  const submit = async () => {
    if (!title.trim() || !bubbleId) return;
    setBusy(true);
    try {
      await api.post('/clubs', {
        title: title.trim(), theme, bubble_id: bubbleId, duration_hours: duration,
      });
      toast.success('club opened.');
      onCreated?.(); onClose?.();
    } catch (err) { toast.error(err?.response?.data?.detail || 'could not'); }
    finally { setBusy(false); }
  };
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-xl grid place-items-center px-4" onClick={onClose}>
      <div className="w-full max-w-md bg-neutral-950 rounded-3xl p-6 relative ring-1 ring-fuchsia-500/30" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 p-2 rounded-full hover:bg-white/10 text-white/60"><X size={16} weight="bold" /></button>
        <div className="text-center mb-5">
          <MusicNotes size={28} weight="duotone" className="text-fuchsia-400 mx-auto" />
          <div className="font-display font-bold text-xl mt-2">Open a club</div>
          <div className="text-xs text-white/55 mt-1">21+ only. Adult swarms. Bubbles in heat.</div>
        </div>
        <input value={title} onChange={(e) => setTitle(e.target.value.slice(0, 60))}
          data-testid="club-title"
          placeholder="A name for the night"
          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-sm mb-3 focus:outline-none focus:border-fuchsia-500" />
        <div className="grid grid-cols-2 gap-1.5 mb-3">
          {THEMES.map((t) => (
            <button key={t.id}
              data-testid={`club-theme-${t.id}`}
              onClick={() => setTheme(t.id)}
              className={`rounded-xl py-2 text-xs font-bold border transition ${
                theme === t.id ? 'border-fuchsia-500 bg-fuchsia-500/10' : 'border-white/10 text-white/60 hover:border-white/30'
              }`}>{t.label}</button>
          ))}
        </div>
        <select value={bubbleId} onChange={(e) => setBubbleId(e.target.value)}
          data-testid="club-bubble"
          className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm focus:outline-none mb-3">
          {bubbles.map((b) => (<option key={b.id} value={b.id}>{b.name}</option>))}
        </select>
        <div className="text-[10px] uppercase tracking-widest text-white/60 mb-1">duration · hours</div>
        <input type="number" min="1" max="72" value={duration}
          onChange={(e) => setDuration(Math.max(1, parseInt(e.target.value || '1')))}
          className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm focus:outline-none mb-4" />
        <button data-testid="club-create" onClick={submit} disabled={busy || !title.trim()}
          className="w-full bg-fuchsia-500 hover:bg-fuchsia-400 text-white py-3 rounded-full font-bold text-sm disabled:opacity-30">
          {busy ? 'opening…' : 'Open the doors'}
        </button>
      </div>
    </div>
  );
};

const Clubs = () => {
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gated, setGated] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);
  const [adultOpen, setAdultOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/clubs/active');
      setClubs(data.clubs || []);
      setGated(false);
    } catch (err) {
      if (err?.response?.status === 403) setGated(true);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="min-h-screen grid place-items-center text-white/60">listening…</div>;

  if (gated) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <Shield size={40} weight="duotone" className="text-fuchsia-400 mx-auto" />
        <h1 className="font-display text-4xl font-black mt-4">The Clubs</h1>
        <p className="text-white/60 mt-2 text-sm">Behind the velvet rope. 21+ only.</p>
        <button onClick={() => setAdultOpen(true)}
          data-testid="clubs-gate-verify"
          className="mt-6 px-6 py-3 rounded-full bg-fuchsia-500 hover:bg-fuchsia-400 text-white text-sm font-bold">
          Verify your age
        </button>
        <AdultGate open={adultOpen} onClose={() => setAdultOpen(false)} onVerified={load} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-neutral-950 via-fuchsia-950/40 to-black">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 pb-24">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-fuchsia-300">after-dark · 21+</div>
            <h1 className="font-display text-4xl sm:text-5xl font-black mt-1 bg-gradient-to-r from-fuchsia-300 to-amber-200 bg-clip-text text-transparent">
              The Clubs
            </h1>
            <p className="text-white/60 mt-2 text-sm max-w-xl">
              Themed adult swarms — jazz lounges, techno basements, rooftops at 3am.
              Bubbles arrive carrying after-hours secrets.
            </p>
          </div>
          <button data-testid="open-create-club"
            onClick={() => setOpenCreate(true)}
            className="bg-fuchsia-500 hover:bg-fuchsia-400 px-5 py-3 rounded-full text-sm font-bold text-white flex items-center gap-2">
            <Plus size={14} weight="bold" /> Open a club
          </button>
        </div>

        {clubs.length === 0 ? (
          <div className="rounded-3xl bg-black/40 ring-1 ring-fuchsia-500/20 p-10 text-center">
            <MoonStars size={28} weight="duotone" className="text-fuchsia-300 mx-auto mb-2" />
            <div className="text-white/60">No clubs open right now. Be the host.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {clubs.map((c) => {
              const t = THEMES.find((x) => x.id === c.theme) || THEMES[0];
              return (
                <motion.div key={c.id} data-testid={`club-card-${c.id}`}
                  whileHover={{ y: -2 }}
                  className={`rounded-2xl p-5 ring-1 ring-white/10 relative overflow-hidden`}>
                  <div className={`absolute inset-0 bg-gradient-to-br ${t.glow} opacity-25 blur-2xl pointer-events-none`} />
                  <div className="relative">
                    <div className="text-[10px] uppercase tracking-widest text-white/60">{t.label}</div>
                    <div className="font-display text-xl font-bold mt-1 leading-snug">{c.title}</div>
                    <div className="text-[10px] uppercase tracking-widest text-white/60 mt-2">
                      hosted by @{c.host_username} · {fmtAgo(c.created_at)}
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-xs text-white/70">{(c.members || []).length} bubble{(c.members||[]).length===1?'':'s'} inside</span>
                      <Link to={`/swarm/${c.id}`}
                        className="px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-xs font-bold">
                        Enter
                      </Link>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        <CreateClubModal open={openCreate} onClose={() => setOpenCreate(false)} onCreated={load} />
      </div>
    </div>
  );
};

export default Clubs;
